import { common } from '@/features/i18n/common';
import { t } from '@/features/i18n/utils';
import { Suspense, useState } from 'react';
import { useSetAtom } from 'jotai';
import { Spinner } from '@/ui/components/spinner';
import { ProjectBreadcrumbs } from '@/features/projects/components/project-breadcrumbs';
import { createFileRoute } from '@tanstack/react-router';
import z from 'zod';
import { useLocale } from 'use-intl';
import { Header, HeaderChatButton, HeaderOrganizationMenu } from '@/ui/components/header';
import {
  MainScrollArea,
  MainHeader,
  MainNav,
  MainNavList,
  MainNavItem,
  MainNavLink,
  MainNavButton,
  Main,
  MainActions,
  MainHeaderCopyButton,
  MainNavCompanyItem,
} from '@/ui/components/main';
import { H1 } from '@/ui/components/heading';
import { WorkspacesList } from '@/features/workspaces/components/workspaces-list';
import { WorkspacesDisplayMenu } from '@/features/workspaces/components/workspaces-display-menu';
import { projectDetailsQuery } from '@/features/projects/queries';
import { ProjectModeEnum } from '@/features/projects/constants';
import { ProjectTypeIcon } from '@/features/projects/components/project-type-icon';
import { toProjectType } from '@/features/projects/utils';
import { Icon } from '@/ui/components/icon';
import {
  SettingsIcon,
  SquareLock01Icon,
  UserGroupIcon,
  Clock04Icon,
  MapsGlobal01Icon,
} from '@hugeicons-pro/core-stroke-rounded';
import { noop, useSuspenseQuery } from '@tanstack/react-query';
import { getCountryLabel } from '@/features/projects/countries';
import { chatSidePanelOpenAtom } from '@/features/chat/store';
import { ActivityPanel } from '@/ui/components/activity-panel';
import { ProjectActivity } from '@/features/projects/components/project-activity';
import { SuspenseBoundary } from '@/ui/components/suspense-boundary';
import { useAuth } from '@/features/auth';
import { UserRole } from '@/features/user/constants';
import { workspaceTermsQuery } from '@/features/workspace-terms/queries';
import { WorkspaceLimitAlert } from '@/features/workspace-terms/components/workspace-limit-alert';

export const Route = createFileRoute('/_authed/project/$projectId/')({
  component: RouteComponent,
  loader: ({ context }) => {
    if (context.auth.user.organizationRole === UserRole.ORGANIZATION_ADMIN)
      context.queryClient.query(workspaceTermsQuery(context.auth.user.organizationId)).catch(noop);
  },
  validateSearch: z.object({
    page: z.number().optional(),
    pageSize: z.number().optional(),
    tenderPage: z.number().optional(),
    tenderPageSize: z.number().optional(),
    contractPage: z.number().optional(),
    contractPageSize: z.number().optional(),
  }),
});

function RouteComponent() {
  const { projectId } = Route.useParams();
  const locale = useLocale();
  const { data: project } = useSuspenseQuery(projectDetailsQuery(projectId));
  const projectType = toProjectType(project.type);
  const [activityOpen, setActivityOpen] = useState(false);
  const setChatOpen = useSetAtom(chatSidePanelOpenAtom);
  const auth = useAuth();

  return (
    <>
      <Main className="flex-row">
        <div className="relative grow flex flex-col min-w-0 min-h-0">
          <Header>
            <HeaderOrganizationMenu />
            <ProjectBreadcrumbs />
            <HeaderChatButton />
          </Header>
          <MainScrollArea viewportClassName="flex flex-col @container">
            {auth.user.organizationRole === UserRole.ORGANIZATION_ADMIN ? (
              <SuspenseBoundary>
                <WorkspaceLimitAlert />
              </SuspenseBoundary>
            ) : null}
            <div className="border-b flex items-center gap-y-3 md:py-3">
              <MainHeader className="justify-start pr-0 flex-nowrap w-fit @3xl:max-w-1/2 py-0 max-md:hidden md:mr-auto">
                <H1 className="min-w-0 max-w-md @3xl:max-w-none">
                  <span className="line-clamp-1">{project.name}</span>
                </H1>
                <MainHeaderCopyButton className="shrink-0" />
              </MainHeader>
              <MainNav>
                <MainNavList className="md:pb-2">
                  {project.type && (
                    <MainNavItem className="text-muted items-center whitespace-nowrap px-1.75 h-7 gap-1.5 flex text-sm">
                      <ProjectTypeIcon type={projectType} />
                      {projectType ? t(common.projectType[projectType]) : project.type}
                    </MainNavItem>
                  )}
                  {project.countryCode && (
                    <MainNavItem className="text-muted items-center whitespace-nowrap px-1.75 h-7 gap-1.5 flex text-sm">
                      <Icon icon={MapsGlobal01Icon} />
                      {getCountryLabel(project.countryCode, locale)}
                    </MainNavItem>
                  )}
                  <MainNavCompanyItem />
                  {project.mode === ProjectModeEnum.OPEN ? null : (
                    <MainNavItem>
                      <MainNavLink to="/project/$projectId/members" params={{ projectId }}>
                        <Icon icon={UserGroupIcon} />
                        {project.numberOfUsers} {t({ en: 'Members', nb: 'Medlemmer' })}
                        <span className="flex items-center gap-0">
                          (<span className="pl-px">{t({ en: 'restricted', nb: 'begrenset' })}</span>
                          <Icon icon={SquareLock01Icon} className="size-3.5! ml-0.5" />)
                        </span>
                      </MainNavLink>
                    </MainNavItem>
                  )}
                  <MainNavItem>
                    <MainNavButton
                      data-selected={activityOpen ? '' : undefined}
                      onClick={() => {
                        setActivityOpen(!activityOpen);
                        if (!activityOpen) setChatOpen(false);
                      }}
                    >
                      <Icon icon={Clock04Icon} />
                      {t({ en: 'Activity log', nb: 'Aktivitetslogg' })}
                    </MainNavButton>
                  </MainNavItem>
                  <MainNavItem>
                    <MainNavLink to="/project/$projectId/settings" params={{ projectId }}>
                      <Icon icon={SettingsIcon} />
                      {t({ en: 'Settings', nb: 'Innstillinger' })}
                    </MainNavLink>
                  </MainNavItem>
                </MainNavList>
              </MainNav>
            </div>
            <MainActions className="flex pl-1.5 border-t-0!">
              <WorkspacesDisplayMenu />
            </MainActions>
            <Suspense fallback={<Spinner kind="inset" />}>
              <WorkspacesList />
            </Suspense>
          </MainScrollArea>
        </div>
        <ActivityPanel open={activityOpen} onOpenChange={setActivityOpen}>
          <ProjectActivity />
        </ActivityPanel>
      </Main>
    </>
  );
}
